import { forwardRef, memo, useState, useCallback, type ReactNode } from 'react';
import { type RouteObject } from 'react-router-dom';
// local imports
import type { BreadcrumbItem, DrawerItem } from '../types';
import useCurrentPath from '../hooks/useCurrentPath';
import AppBar, { type AppBarVariant, APPBAR_EMPTY } from './AppBar';
import AppDrawer from './AppDrawer';

type AppHeaderProps = {
  readonly logo: ReactNode;
  readonly appbar: typeof AppBar;
  readonly drawer: typeof AppDrawer;
  readonly items: DrawerItem[];
  readonly routes: RouteObject[];
  readonly breadcrumbs?: BreadcrumbItem[] | null;
  readonly variant?: AppBarVariant;
  // for Storybook only:
  readonly testOpen?: boolean;
};

const AppHeader = forwardRef<HTMLDivElement, AppHeaderProps>(({
  logo,
  appbar: CustomizedAppBar,
  drawer: CustomizedAppDrawer,
  items,
  routes,
  breadcrumbs,
  variant = 'full',
  testOpen = false,
  ...props
}, ref) => {
  const path = useCurrentPath(routes);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(testOpen);

  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  const withDrawer = variant !== APPBAR_EMPTY;

  return (
    <>
      <CustomizedAppBar
          ref={ref}
          logo={logo}
          path={path}
          breadcrumbs={breadcrumbs || undefined}
          toggleDrawer={handleDrawerOpen}
          drawerOpen={withDrawer && drawerOpen ? true : undefined}
          variant={variant}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
      />
      {withDrawer ? (
        <CustomizedAppDrawer
            items={items}
            path={path}
            open={drawerOpen}
            onOpen={handleDrawerOpen}
            onClose={handleDrawerClose}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
        />
      ) : undefined}
    </>
  );
});

AppHeader.displayName = 'AppHeader';

export default memo(AppHeader);
